import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import moment from 'moment';

import { CfdiIndexEntry, CfdiIndexGroup } from '../../../models/CfdiIndex';
import { graphqlSchema } from '../../../services/graphql.schema';
import { buildCfdiDownloadUrl } from '../../../services/fiscalpopApi';
import { formatCurrencyStyled } from '../../visuals/currency.formatted';
import { calculateTotal } from '../../../utils/calculate.conceptos';
import Loading from '../../Animations/loadScreen';
import { entryRoleLabel, entryTypeLabel } from '../facturas.actions';
import { canCancelEntry, canEditEntry, StatusBadge } from '../Facturas.row';

import './Facturas.group.scss';

interface GroupDialogProps {
    argumentId: string;
    selectedUuid: string;
    authToken: string;
    busyUuid: string;
    requestClose: () => void;
    onAskCancel: (entry: CfdiIndexEntry) => void;
    onAskEdit: (entry: CfdiIndexEntry) => void;
    onFilterUuid: (uuid: string) => void;
}

const ARGUMENT_LABEL: Record<number, string> = {
    0: 'Factura',
    1: 'Factura con adelanto',
    2: 'Pago en parcialidades',
    3: 'Parcialidades con adelanto',
};

function FacturasGroup({ argumentId, selectedUuid, authToken, busyUuid, requestClose, onAskCancel, onAskEdit, onFilterUuid }: GroupDialogProps) {
    const { data, loading, error } = useQuery<{ getCfdiIndexGroup: CfdiIndexGroup }>(graphqlSchema.FISCALPOP.CFDI.getCfdiIndexGroup, {
        variables: { argumentId },
        skip: !argumentId,
        fetchPolicy: 'network-only',
    });

    const group = data?.getCfdiIndexGroup;
    const argument = group?.argument;
    const master = argument?.MasterCFDI;
    const masterTotal = master?.conceptos?.length ? calculateTotal(master.conceptos).total : null;
    const cfdis = (group?.cfdis || []).slice().sort((a, b) => a.created - b.created);
    const expectedPayments = argument?.paymentEvents?.length || 0;
    const stampedPayments = cfdis.filter(c => c.kind === 'pago' && c.status !== 'sustituida' && c.status !== 'cancelada').length;

    return (
        <div id="FacturasGroup" className="card table">
            <div className="cardTitle withClose">
                <h4>Relación de facturas</h4>
                <i className="material-icons" onClick={requestClose}>close</i>
            </div>
            <div className="cardBody">
                <Loading display={loading} relativePos={true} />
                {
                    error ?
                        <p className="_error">No fue posible cargar la relación: {error.graphQLErrors?.[0]?.message || error.message}</p>
                        : null
                }
                {
                    argument ?
                        <div className="_header">
                            <div className="row two gap-compressed">
                                <p className="_bold">{argument.specialType === 'cartaporte' ? 'Carta porte' : (ARGUMENT_LABEL[argument.multipayType] || 'Factura')}</p>
                                <p className="_bold end">{masterTotal !== null ? formatCurrencyStyled(masterTotal) : ''}</p>
                            </div>
                            <div className="row two gap-compressed">
                                <p className="_info">{argument.createdAt ? moment(argument.createdAt).format('DD MMM YYYY') : ''}</p>
                                <p className="_info end">{master?.receptor?.nombre || ''}{master?.receptor?.rfc ? ` · ${master.receptor.rfc}` : ''}</p>
                            </div>
                            {
                                expectedPayments ?
                                    <p className="_info">Parcialidades: {stampedPayments} de {expectedPayments} emitidas{argument.isClosed ? ' · Cerrada' : ''}</p>
                                    : null
                            }
                        </div>
                        : null
                }
                <div className="_list">
                    {
                        cfdis.map(entry => {
                            const isSelected = entry.uuid === selectedUuid;
                            const cancelable = canCancelEntry(entry) && !busyUuid;
                            const editable = canEditEntry(entry) && !busyUuid;
                            return (
                                <div className={`_groupEntry ${isSelected ? '_selected' : ''} _st_${entry.status}`} key={entry.uuid}>
                                    <div className="_main">
                                        <p className="_bold">{entryRoleLabel(entry) || entryTypeLabel(entry)}</p>
                                        <p className="_bold end">{formatCurrencyStyled(entry.total || 0)}</p>
                                    </div>
                                    <div className="_meta">
                                        <p className="_info">{moment(entry.created).format('DD MMM YYYY HH:mm')}</p>
                                        <p className="_info end">{entry.serie || '—'} - {entry.folio || '—'}</p>
                                    </div>
                                    <p className={`_info _uuid ${entry.status !== 'vigente' ? '_off' : ''}`}>{entry.uuid}</p>
                                    <div className="_footer">
                                        <StatusBadge entry={entry} onFilterUuid={(uuid) => { onFilterUuid(uuid); requestClose(); }} />
                                        <div className="_actions">
                                            <a className="svg violet" title="Descargar PDF" rel="noopener noreferrer" href={buildCfdiDownloadUrl(authToken, entry.uuid, 'pdf')} download={true}>
                                                <i className="material-icons">picture_as_pdf</i>
                                            </a>
                                            <a className="svg violet" title="Descargar XML" rel="noopener noreferrer" href={buildCfdiDownloadUrl(authToken, entry.uuid, 'xml')} download={true}>
                                                <i className="material-icons">code</i>
                                            </a>
                                            <button className="svg yellow" title="Editar" onClick={() => onAskEdit(entry)} disabled={!editable}>
                                                <i className="material-icons">create</i>
                                            </button>
                                            <button className="svg red" title="Cancelar" onClick={() => onAskCancel(entry)} disabled={!cancelable}>
                                                <i className="material-icons">delete</i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                    {
                        !loading && argument && !cfdis.length ?
                            <p className="_info _empty">Esta relación no tiene CFDIs indexados todavía.</p>
                            : null
                    }
                </div>
            </div>
        </div>
    );
}

export default FacturasGroup;
